import React from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  NiimbotBluetoothClient,
  ImageEncoder,
  LabelType,
  modelsLibrary,
} from "@mmote/niimbluelib";

/** B1 printhead width (203 dpi). */
export const B1_PRINTHEAD_PX = 384;
export const B1_DPI = 203;
export const PRINT_DIRECTION = "top";
export const MODEL_FALLBACK = "B1";

/** @deprecated Use resolveLabelSize — kept for callers expecting 50×30 defaults */
export const LABEL_W_PX = 384;
/** @deprecated Use resolveLabelSize */
export const LABEL_H_PX = 240;

/** Niim Bot BLE GATT service used by niimbluelib. */
export const NIIMBOT_BLE_SERVICE =
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2";

/**
 * Build Web Bluetooth name filters for Niim Bot devices.
 * Uses 2–3 char model prefixes (not single letters — those match headphones etc.).
 * Includes K3… names some units advertise as.
 */
export function buildNiimbotBleFilters() {
  const prefixes = new Set([
    "B1",
    "B2",
    "B3",
    "B11",
    "B18",
    "B21",
    "D11",
    "D110",
    "Niim",
    "NIIM",
    "NiiM",
    "K3",
    "A1",
    "A8",
    "A20",
  ]);

  try {
    (modelsLibrary || []).forEach((meta) => {
      const model = String(meta?.model || "").replace(/_/g, "");
      if (model.length >= 2) prefixes.add(model.slice(0, 2));
      if (model.length >= 3) prefixes.add(model.slice(0, 3));
    });
  } catch {
    // modelsLibrary optional
  }

  return [
    ...[...prefixes].map((namePrefix) => ({ namePrefix })),
    // Devices that advertise the Niimbot GATT service (even if name is odd)
    { services: [NIIMBOT_BLE_SERVICE] },
  ];
}

/** Default filtered picker (preferred). */
export const NIIMBOT_BLE_FILTERS = buildNiimbotBleFilters();

/** Preset label sizes in mm (B1-oriented). */
export const LABEL_SIZE_PRESETS = [
  { id: "40x40", label: "40×40", wMm: 40, hMm: 40 },
  { id: "50x30", label: "50×30", wMm: 50, hMm: 30 },
  { id: "50x40", label: "50×40", wMm: 50, hMm: 40 },
  { id: "60x40", label: "60×40", wMm: 60, hMm: 40 },
  { id: "40x30", label: "40×30", wMm: 40, hMm: 30 },
  { id: "80x50", label: "80×50", wMm: 80, hMm: 50 },
  { id: "custom", label: "Custom", wMm: 50, hMm: 30, custom: true },
];

export const DEFAULT_LABEL_SIZE_ID = "40x40";
export const DEFAULT_PRINT_DENSITY = 3;

const UNSUPPORTED_MESSAGE =
  "Niim Bot printing requires Chrome or Edge over HTTPS (or localhost).";

/** @type {NiimbotBluetoothClient | null} */
let sharedClient = null;

/**
 * @param {number} mm
 * @returns {number}
 */
export function mmToPx(mm) {
  return Math.round((Number(mm) * B1_DPI) / 25.4);
}

/**
 * Round down to multiple of 8 (ImageEncoder requirement).
 * @param {number} n
 */
function floorTo8(n) {
  return Math.max(8, Math.floor(n / 8) * 8);
}

/**
 * Resolve label mm size to printable canvas pixels for B1 (203 dpi).
 * Width is clamped to printhead; height is NOT scaled with width (preserves selected size).
 * @param {number} wMm
 * @param {number} hMm
 * @returns {{ wMm: number, hMm: number, wPx: number, hPx: number }}
 */
export function resolveLabelSize(wMm, hMm) {
  const w = Math.max(10, Number(wMm) || 50);
  const h = Math.max(10, Number(hMm) || 30);
  let wPx = mmToPx(w);
  let hPx = mmToPx(h);

  // Printhead max only — do not shrink height or the printed label length is wrong
  if (wPx > B1_PRINTHEAD_PX) {
    wPx = B1_PRINTHEAD_PX;
  }

  return {
    wMm: w,
    hMm: h,
    wPx: floorTo8(wPx),
    hPx: floorTo8(hPx),
  };
}

/**
 * Force pure black/white pixels (thermal printers + scanners need hard edges).
 * @param {HTMLCanvasElement} canvas
 */
function thresholdToBlackWhite(canvas) {
  const ctx = canvas.getContext("2d");
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = image.data;
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    const v = lum < 180 ? 0 : 255;
    data[i] = v;
    data[i + 1] = v;
    data[i + 2] = v;
    data[i + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
}

/**
 * Preview-only: approximate how dark a Niim Bot density (1–5) heat setting looks.
 * Does not change the bitmap used for actual print/download.
 * @param {HTMLCanvasElement} sourceCanvas
 * @param {number} [density=DEFAULT_PRINT_DENSITY]
 * @returns {HTMLCanvasElement}
 */
export function simulatePrintDensityPreview(
  sourceCanvas,
  density = DEFAULT_PRINT_DENSITY
) {
  const level = Math.min(
    5,
    Math.max(1, Math.round(Number(density) || DEFAULT_PRINT_DENSITY))
  );
  const out = document.createElement("canvas");
  out.width = sourceCanvas.width;
  out.height = sourceCanvas.height;
  const ctx = out.getContext("2d");
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(sourceCanvas, 0, 0);

  // Below default (3): fade black → grey (lighter burn).
  // At/above 3: pure black; level 5 slightly thickens dark pixels.
  const ink =
    level <= 3
      ? Math.round((160 * (3 - level)) / 2) // 1→160, 2→80, 3→0
      : 0;

  const image = ctx.getImageData(0, 0, out.width, out.height);
  const data = image.data;
  const isDark = new Uint8Array(out.width * out.height);

  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    if (lum < 200) {
      isDark[p] = 1;
      data[i] = ink;
      data[i + 1] = ink;
      data[i + 2] = ink;
    }
  }

  if (level >= 4) {
    // One-pixel dilation so high density reads as “heavier” ink
    const w = out.width;
    const h = out.height;
    const copy = new Uint8ClampedArray(data);
    for (let y = 1; y < h - 1; y += 1) {
      for (let x = 1; x < w - 1; x += 1) {
        const p = y * w + x;
        if (isDark[p]) continue;
        const neighborDark =
          isDark[p - 1] ||
          isDark[p + 1] ||
          isDark[p - w] ||
          isDark[p + w] ||
          (level >= 5 &&
            (isDark[p - w - 1] ||
              isDark[p - w + 1] ||
              isDark[p + w - 1] ||
              isDark[p + w + 1]));
        if (neighborDark) {
          const i = p * 4;
          copy[i] = 0;
          copy[i + 1] = 0;
          copy[i + 2] = 0;
        }
      }
    }
    data.set(copy);
  }

  ctx.putImageData(image, 0, 0);
  return out;
}

/**
 * Map Web Bluetooth / connect errors to staff-friendly messages.
 * @param {unknown} err
 * @returns {string}
 */
export function formatNiimbotConnectError(err) {
  const name = err?.name || "";
  const msg = String(err?.message || "").toLowerCase();

  if (
    name === "NotFoundError" ||
    msg.includes("cancelled") ||
    msg.includes("canceled") ||
    msg.includes("user cancelled") ||
    msg.includes("user canceled")
  ) {
    return "Pairing cancelled. Turn on your Niim Bot B1 and try again.";
  }
  if (name === "SecurityError" || msg.includes("secure")) {
    return UNSUPPORTED_MESSAGE;
  }
  if (name === "NetworkError" || msg.includes("gatt")) {
    return "Could not stay connected to the printer. Keep it nearby and powered on.";
  }
  if (msg.includes("characteristic")) {
    return "That device is not a compatible Niim Bot printer. Pick a B1 from the list.";
  }
  return err?.message || "Could not connect to Niim Bot";
}

/**
 * Temporarily override navigator.bluetooth.requestDevice while connecting.
 * @param {() => Promise<T>} fn
 * @param {{ showAllDevices?: boolean }} [opts]
 * @template T
 * @returns {Promise<T>}
 */
async function withNiimbotBleFilters(fn, opts = {}) {
  const bluetooth = navigator?.bluetooth;
  if (!bluetooth || typeof bluetooth.requestDevice !== "function") {
    return fn();
  }

  const showAllDevices = !!opts.showAllDevices;
  const original = bluetooth.requestDevice.bind(bluetooth);
  bluetooth.requestDevice = (options = {}) => {
    const optionalServices = [
      ...new Set([
        ...(options.optionalServices || []),
        NIIMBOT_BLE_SERVICE,
      ]),
    ];

    if (showAllDevices) {
      // Fallback when the printer name does not match known prefixes
      return original({
        acceptAllDevices: true,
        optionalServices,
      });
    }

    return original({
      ...options,
      filters: buildNiimbotBleFilters(),
      optionalServices,
    });
  };

  try {
    return await fn();
  } finally {
    bluetooth.requestDevice = original;
  }
}

/**
 * Web Bluetooth + secure context check for Niim Bot printing.
 * @returns {{ supported: boolean, message?: string }}
 */
export function isNiimbotSupported() {
  if (typeof window === "undefined") {
    return { supported: false, message: UNSUPPORTED_MESSAGE };
  }
  const hasBluetooth =
    typeof navigator !== "undefined" && !!navigator.bluetooth;
  const isSecure =
    window.isSecureContext === true ||
    window.location?.hostname === "localhost" ||
    window.location?.hostname === "127.0.0.1";

  if (!hasBluetooth || !isSecure) {
    return { supported: false, message: UNSUPPORTED_MESSAGE };
  }
  return { supported: true };
}

/**
 * @returns {NiimbotBluetoothClient | null}
 */
export function getNiimbotClient() {
  return sharedClient;
}

/**
 * @returns {boolean}
 */
export function isNiimbotConnected() {
  return !!(sharedClient && sharedClient.isConnected());
}

/**
 * Pair and connect a Niim Bot over Web Bluetooth. Reuses session client when already connected.
 * @param {{ showAllDevices?: boolean }} [options]
 * @returns {Promise<NiimbotBluetoothClient>}
 */
export async function connectNiimbot(options = {}) {
  const support = isNiimbotSupported();
  if (!support.supported) {
    throw new Error(support.message || UNSUPPORTED_MESSAGE);
  }

  if (sharedClient && sharedClient.isConnected()) {
    return sharedClient;
  }

  if (sharedClient) {
    try {
      await sharedClient.disconnect();
    } catch {
      // ignore
    }
    sharedClient = null;
  }

  const client = new NiimbotBluetoothClient();
  client.setPacketInterval(2);
  await withNiimbotBleFilters(() => client.connect(), {
    showAllDevices: !!options.showAllDevices,
  });
  sharedClient = client;
  return client;
}

/**
 * Disconnect and clear the shared BLE session.
 * @returns {Promise<void>}
 */
export async function disconnectNiimbot() {
  if (!sharedClient) return;
  const client = sharedClient;
  sharedClient = null;
  try {
    if (client.isConnected()) {
      await client.disconnect();
    }
  } catch {
    // ignore
  }
}

/**
 * True when the QR canvas has actually been drawn (not an empty mount).
 * @param {HTMLCanvasElement} canvas
 */
function canvasHasDarkPixels(canvas) {
  if (!canvas?.width || !canvas?.height) return false;
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  const sample = Math.min(48, canvas.width, canvas.height);
  const sx = Math.floor((canvas.width - sample) / 2);
  const sy = Math.floor((canvas.height - sample) / 2);
  const { data } = ctx.getImageData(sx, sy, sample, sample);
  for (let i = 0; i < data.length; i += 16) {
    if (data[i] < 200 || data[i + 1] < 200 || data[i + 2] < 200) return true;
  }
  return false;
}

function waitAnimationFrame() {
  return new Promise((resolve) => requestAnimationFrame(resolve));
}

let qrRenderQueue = Promise.resolve();

function renderQrToCanvas(qrUrl, size) {
  const job = qrRenderQueue.then(
    () => renderQrToCanvasNow(qrUrl, size),
    () => renderQrToCanvasNow(qrUrl, size)
  );
  qrRenderQueue = job.then(
    () => undefined,
    () => undefined
  );
  return job;
}

function renderQrToCanvasNow(qrUrl, size) {
  return new Promise((resolve, reject) => {
    if (!qrUrl) {
      reject(new Error("Missing QR URL"));
      return;
    }

    // Render larger then downscale with nearest-neighbor for crisp modules
    const renderSize = Math.max(size, 256);

    const host = document.createElement("div");
    host.style.cssText =
      "position:fixed;left:-10000px;top:0;width:280px;height:280px;overflow:hidden;pointer-events:none;";
    document.body.appendChild(host);

    const root = createRoot(host);

    const finish = (err, canvas) => {
      try {
        root.unmount();
      } catch {
        // ignore
      }
      if (host.parentNode) {
        host.parentNode.removeChild(host);
      }
      if (err) reject(err);
      else resolve(canvas);
    };

    try {
      flushSync(() => {
        root.render(
          React.createElement(QRCodeCanvas, {
            value: qrUrl,
            size: renderSize,
            includeMargin: true,
            level: "H",
            bgColor: "#FFFFFF",
            fgColor: "#000000",
          })
        );
      });
    } catch (e) {
      finish(e);
      return;
    }

    const copyWhenReady = async () => {
      try {
        let source = host.querySelector("canvas");
        for (let i = 0; i < 30; i += 1) {
          if (source && canvasHasDarkPixels(source)) break;
          await waitAnimationFrame();
          source = host.querySelector("canvas");
        }
        if (!source || !canvasHasDarkPixels(source)) {
          finish(new Error("QR canvas failed to render"));
          return;
        }
        const clone = document.createElement("canvas");
        clone.width = size;
        clone.height = size;
        const ctx = clone.getContext("2d");
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, size, size);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(source, 0, 0, size, size);
        thresholdToBlackWhite(clone);
        finish(null, clone);
      } catch (e) {
        finish(e);
      }
    };

    copyWhenReady();
  });
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} maxWidth
 */
function fitText(ctx, text, maxWidth) {
  const value = String(text || "").trim();
  if (!value) return "";
  if (ctx.measureText(value).width <= maxWidth) return value;
  let truncated = value;
  while (truncated.length > 1 && ctx.measureText(`${truncated}…`).width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated}…`;
}

/**
 * Draw clean bold text (no stroke — stroke looks jagged at low DPI).
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} x
 * @param {number} y
 * @param {{ fontSize?: number, maxWidth?: number }} [opts]
 */
function drawLabelText(ctx, text, x, y, opts = {}) {
  const value = String(text || "").trim();
  if (!value) return;

  const fontSize = Math.max(18, opts.fontSize || 22);
  const maxWidth = opts.maxWidth || 9999;
  ctx.font = `bold ${fontSize}px Arial, Helvetica, sans-serif`;
  const line = fitText(ctx, value, maxWidth);

  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillStyle = "#000000";
  ctx.fillText(line, x, y);
}

/**
 * Build label canvas via 3× supersample → B/W threshold → sharp downscale.
 * This keeps both QR and text clean at 203 DPI.
 * @param {{
 *   qrUrl: string,
 *   tableName?: string,
 *   serviceAreaName?: string,
 *   companyName?: string,
 *   wMm?: number,
 *   hMm?: number,
 *   showTableName?: boolean,
 *   showServiceArea?: boolean,
 *   showCompanyName?: boolean,
 *   showScanHint?: boolean,
 * }} params
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function buildTableQrLabelCanvas({
  qrUrl,
  tableName = "",
  serviceAreaName = "",
  companyName = "",
  wMm = 50,
  hMm = 30,
  showTableName = true,
  showServiceArea = false,
  showCompanyName = false,
  showScanHint = false,
}) {
  const { wPx, hPx } = resolveLabelSize(wMm, hMm);
  const SCALE = 3;
  const hiW = wPx * SCALE;
  const hiH = hPx * SCALE;

  const hi = document.createElement("canvas");
  hi.width = hiW;
  hi.height = hiH;
  const ctx = hi.getContext("2d");

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, hiW, hiH);

  const pad = Math.max(12, Math.round(Math.min(hiW, hiH) * 0.045));
  const primarySize = Math.max(36, Math.min(54, Math.round(hiH * 0.08)));
  const secondarySize = Math.max(28, Math.min(42, Math.round(hiH * 0.065)));
  const primaryLineH = primarySize + 8;
  const secondaryLineH = secondarySize + 8;

  const topLines = [];
  if (showTableName && tableName) {
    topLines.push({ text: tableName, primary: true });
  }
  if (showServiceArea && serviceAreaName) {
    topLines.push({ text: serviceAreaName, primary: false });
  }

  const bottomLines = [];
  if (showCompanyName && companyName) {
    bottomLines.push({ text: companyName, primary: true });
  }
  if (showScanHint) {
    bottomLines.push({ text: "Scan for options", primary: false });
  }

  const topBlockH = topLines.reduce(
    (sum, line) => sum + (line.primary ? primaryLineH : secondaryLineH),
    0
  );
  const bottomBlockH = bottomLines.reduce(
    (sum, line) => sum + (line.primary ? primaryLineH : secondaryLineH),
    0
  );

  const textGap = Math.max(8, Math.round(Math.min(hiW, hiH) * 0.02));
  const topGap = topLines.length ? textGap : 0;
  const bottomGap = bottomLines.length ? textGap : 0;

  const availableForQr = Math.max(
    80,
    hiH - pad * 2 - topBlockH - bottomBlockH - topGap - bottomGap
  );
  const qrSize = Math.max(80, Math.min(hiW - pad * 2, availableForQr));

  const contentH = topBlockH + topGap + qrSize + bottomGap + bottomBlockH;
  let y = Math.max(pad, Math.floor((hiH - contentH) / 2));
  const textMaxW = hiW - pad * 2;

  topLines.forEach((line) => {
    drawLabelText(ctx, line.text, hiW / 2, y, {
      fontSize: line.primary ? primarySize : secondarySize,
      maxWidth: textMaxW,
    });
    y += line.primary ? primaryLineH : secondaryLineH;
  });

  y += topGap;

  const qrCanvas = await renderQrToCanvas(qrUrl, qrSize);
  const qrX = Math.floor((hiW - qrSize) / 2);
  const qrY = y;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(qrCanvas, qrX, qrY);

  let textY = qrY + qrSize + bottomGap;
  bottomLines.forEach((line) => {
    drawLabelText(ctx, line.text, hiW / 2, textY, {
      fontSize: line.primary ? primarySize : secondarySize,
      maxWidth: textMaxW,
    });
    textY += line.primary ? primaryLineH : secondaryLineH;
  });

  // Threshold at high resolution so gray font edges become clean solid black
  thresholdToBlackWhite(hi);

  // Downscale to printer pixels with smoothing, then hard-threshold again
  const canvas = document.createElement("canvas");
  canvas.width = wPx;
  canvas.height = hPx;
  const out = canvas.getContext("2d");
  out.fillStyle = "#FFFFFF";
  out.fillRect(0, 0, wPx, hPx);
  out.imageSmoothingEnabled = true;
  out.imageSmoothingQuality = "high";
  out.drawImage(hi, 0, 0, wPx, hPx);
  thresholdToBlackWhite(canvas);

  return canvas;
}

/**
 * Encode and print one label canvas on the connected Niim Bot.
 * @param {NiimbotBluetoothClient} client
 * @param {HTMLCanvasElement} canvas
 * @param {number} [copies=1]
 * @param {{ density?: number }} [options]
 * @returns {Promise<void>}
 */
export async function printLabelCanvas(client, canvas, copies = 1, options = {}) {
  if (!client || !client.isConnected()) {
    throw new Error("Niim Bot is not connected");
  }
  if (!canvas || !canvas.width || !canvas.height) {
    throw new Error("Invalid label canvas");
  }

  const quantity = Math.max(1, copies | 0);
  const density = Math.min(5, Math.max(1, Number(options.density) || DEFAULT_PRINT_DENSITY));
  const encoded = ImageEncoder.encodeCanvas(canvas, PRINT_DIRECTION);
  const printTaskName = client.getPrintTaskType() ?? MODEL_FALLBACK;

  const printTask = client.abstraction.newPrintTask(printTaskName, {
    totalPages: quantity,
    statusPollIntervalMs: 100,
    statusTimeoutMs: 8_000,
    labelType: LabelType.WithGaps,
    density,
  });

  try {
    await printTask.printInit();
    await printTask.printPage(encoded, quantity);
    if (typeof printTask.waitForPageFinished === "function") {
      await printTask.waitForPageFinished();
    }
    await printTask.waitForFinished();
  } finally {
    try {
      await printTask.printEnd();
    } catch {
      // ensure cleanup even if end fails
    }
  }
}

/**
 * Trigger a PNG download from a canvas.
 * @param {HTMLCanvasElement} canvas
 * @param {string} filename
 */
export function downloadCanvasPng(canvas, filename) {
  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png");
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
